import { Box, Chip, Sheet, Stack, Typography } from "@mui/joy";
import { motion } from "framer-motion";

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';

import StarPicker from "./StarPicker";
import { fmtDateTime, fmtSize, isImage } from "./qualityTheme";

/**
 * Лента вердиктов по событию — что писали в прошлые круги.
 *
 * props:
 *   reviews  — [{ id, verdict, round, stars, comment, created, reviewer, files }]
 *   compact  — плотный режим для карточки
 */
export default function ReviewHistory({ reviews = [], compact = false }) {
  if (!reviews.length) {
    return (
      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
        Проверок ещё не было.
      </Typography>
    );
  }

  return (
    <Stack spacing={compact ? 0.75 : 1}>
      {reviews.map((r, i) => (
        <ReviewItem key={r.id ?? i} review={r} compact={compact} index={i} />
      ))}
    </Stack>
  );
}

export function ReviewItem({ review: r, compact = false, index = 0 }) {
  const rework = r.verdict === 'rework';
  const accent = rework ? '#dc2626' : '#16a34a';
  const soft = rework ? '#fef2f2' : '#f0fdf4';
  const border = rework ? '#fecaca' : '#bbf7d0';

  return (
    <Sheet
      component={motion.div}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: Math.min(index * 0.04, 0.2) }}
      variant="outlined"
      sx={{
        p: compact ? 1.25 : 1.5, borderRadius: '12px',
        bgcolor: soft, borderColor: border,
        borderLeft: '3px solid', borderLeftColor: accent,
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
        {rework
          ? <ReplayRoundedIcon sx={{ fontSize: 16, color: accent }} />
          : <CheckCircleRoundedIcon sx={{ fontSize: 16, color: accent }} />}
        <Typography level="body-xs" sx={{ fontWeight: 800, color: accent }}>
          {rework ? 'На доработку' : 'Принято'}
        </Typography>
        <Chip size="sm" variant="soft" sx={{ '--Chip-minHeight': '18px', fontSize: '0.62rem' }}>
          круг {r.round}
        </Chip>
        <Box sx={{ flex: 1 }} />
        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
          {r.reviewer || '—'} · {fmtDateTime(r.created)}
        </Typography>
      </Stack>

      {r.stars > 0 && (
        <Box sx={{ mb: 0.5 }}>
          <StarPicker value={r.stars} size={15} readOnly />
        </Box>
      )}

      {r.comment && (
        <Typography level="body-sm" sx={{ whiteSpace: 'pre-line', color: 'text.primary' }}>
          {r.comment}
        </Typography>
      )}

      {/* Принял ли чертёжник доработку — по этой отметке видно, сколько
          задача пролежала нетронутой. */}
      {rework && (
        <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
          {r.accepted_at
            ? `Принято чертёжником ${fmtDateTime(r.accepted_at)}`
            : 'Чертёжник ещё не взял в работу'}
        </Typography>
      )}

      {!!(r.files || []).length && <FileStrip files={r.files} />}
    </Sheet>
  );
}

export function FileStrip({ files = [] }) {
  if (!files.length) return null;
  return (
    <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
      {files.map((f) => (
        isImage(f.mime_type, f.name) ? (
          <Box
            key={f.id}
            component={motion.a}
            whileHover={{ scale: 1.04 }}
            href={f.url} target="_blank" rel="noreferrer"
            title={f.name}
            sx={{
              width: 62, height: 62, borderRadius: '10px', overflow: 'hidden',
              border: '1px solid', borderColor: 'neutral.outlinedBorder',
              display: 'block', flexShrink: 0,
            }}
          >
            <Box component="img" src={f.url} alt={f.name}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </Box>
        ) : (
          <Chip
            key={f.id}
            size="sm" variant="outlined"
            component="a" href={f.url} target="_blank" rel="noreferrer"
            startDecorator={<InsertDriveFileRoundedIcon sx={{ fontSize: 14 }} />}
            sx={{ maxWidth: 220, textDecoration: 'none' }}
          >
            {f.name}{f.size ? ` · ${fmtSize(f.size)}` : ''}
          </Chip>
        )
      ))}
    </Stack>
  );
}
